import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowLeft, Reply, ReplyAll, Paperclip, Download, Eye } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import AttachmentViewer from './AttachmentViewer';
import { baseURL } from '@/config/APIConfig';

// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';
const API = baseURL.dashboardApi;


const EmailView = ({ email, onBack, onReply, onReplyAll, folder = 'inbox' }) => {
  const [fullEmail, setFullEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewingAttachment, setViewingAttachment] = useState(null);
  const [viewingAttachmentIndex, setViewingAttachmentIndex] = useState(null);

  useEffect(() => {
    fetchFullEmail();
  }, [email.id, folder]);

  const fetchFullEmail = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/mailbox/email/${email.id}?folder=${folder}`);
      setFullEmail(response.data);
    } catch (error) {
      console.error('Error fetching email:', error);
      toast.error('Failed to load email');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0072FF]"></div>
      </div>
    );
  }

  if (!fullEmail) {
    return (
      <div className="p-8 text-center">
        <p className="text-slate-600 dark:text-slate-400">Failed to load email</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="bg-white dark:bg-slate-900 rounded-xl shadow-sm"
    >
      {/* Header */}
      <div className="p-6 border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={onBack}
            className="flex items-center space-x-2 text-slate-600 dark:text-slate-400 hover:text-[#0072FF] transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Inbox</span>
          </button>
          <div className="flex space-x-2">
            <button
              onClick={() => onReply(fullEmail)}
              className="px-4 py-2 bg-gradient-to-r from-[#0072FF] to-[#00C6FB] text-white rounded-lg hover:shadow-lg hover:scale-105 transition-all flex items-center space-x-2"
            >
              <Reply className="w-4 h-4" />
              <span>Reply</span>
            </button>
            <button
              onClick={() => onReplyAll(fullEmail)}
              className="px-4 py-2 bg-gradient-to-r from-[#00C6FB] to-[#0072FF] text-white rounded-lg hover:shadow-lg hover:scale-105 transition-all flex items-center space-x-2"
            >
              <ReplyAll className="w-4 h-4" />
              <span>Reply All</span>
            </button>
          </div>
        </div>

        {/* Email Info */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
            {fullEmail.subject}
          </h1>
          
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0072FF] to-[#00C6FB] flex items-center justify-center text-white font-semibold">
                  {fullEmail.from_name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-white">
                    {fullEmail.from_name}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-500">
                    {fullEmail.from_email}
                  </p>
                </div>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-500 ml-13">
                To: {fullEmail.to_email}
              </p>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-500">
              {formatDate(fullEmail.date)}
            </p>
          </div>
        </div>
      </div>

      {/* Email Body */}
      <div className="p-6">
        {fullEmail.html_body ? (
          <div 
            className="prose prose-slate dark:prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: fullEmail.html_body }}
          />
        ) : (
          <div className="whitespace-pre-wrap text-slate-700 dark:text-slate-300">
            {fullEmail.body || '(No content)'}
          </div>
        )}
      </div>

      {/* Attachments */}
      {fullEmail.attachments && fullEmail.attachments.length > 0 && (
        <div className="px-6 pb-6">
          <div className="border-t border-slate-200 dark:border-slate-800 pt-4">
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center space-x-2">
              <Paperclip className="w-4 h-4" />
              <span>Attachments ({fullEmail.attachments.length})</span>
            </p>
            <div className="space-y-2">
              {fullEmail.attachments.map((attachment, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <Paperclip className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">
                        {attachment.filename}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-500">
                        {(attachment.size / 1024).toFixed(1)} KB • {attachment.content_type}
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => {
                        setViewingAttachment(attachment);
                        setViewingAttachmentIndex(index);
                      }}
                      className="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
                      title="View"
                    >
                      <Eye className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </button>
                    <a
                      href={`${API}/mailbox/attachment/${fullEmail.id}/${index}`}
                      download={attachment.filename}
                      className="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
                      title="Download"
                    >
                      <Download className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Attachment Viewer Modal */}
      {viewingAttachment && (
        <AttachmentViewer
          emailId={fullEmail.id}
          attachment={viewingAttachment}
          attachmentIndex={viewingAttachmentIndex}
          onClose={() => {
            setViewingAttachment(null);
            setViewingAttachmentIndex(null);
          }}
        />
      )}
    </motion.div>
  );
};

export default EmailView;
